/**
 * LO Comp Profiles (§8.1): identity + effective-dated versions behind ONE
 * "Edit" flow. Any rate change on save creates a NEW version effective on
 * the chosen date (same-day edits supersede — the old version survives only
 * in history); identity changes (name/email/…) update in place and are
 * audited. History drawer shows the version timeline including superseded
 * entries. "Try in calculator" preselects the LO on the Calculator tab.
 */

import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Code,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import Papa from 'papaparse';
import { payrollApi } from '../api';
import { EditorFields, coerceNumericFields } from './EntityManager';
import { profileVersionFields } from './entityConfigs';

const IDENTITY_FIELDS = [
  { key: 'lo_name', label: 'Name', required: true },
  { key: 'email', label: 'Email (canonical, lowercase)', required: true, help: 'Post-alias canonical email — the pipeline Primary/Secondary LO matches against this.' },
  { key: 'is_house_account', label: 'House account', type: 'boolean', checkboxLabel: 'House account: primary comp always $0, pay via Secondary' },
  { key: 'hub_user_uid', label: 'Hub user uid (optional soft link)' },
  { key: 'start_date', label: 'Company start date', type: 'date', help: 'Drives the 90-day new-hire TCPI grace (D-51): self-sourced single-submission fee capped at $200 for closings through day 90. Blank = no grace window.' },
  { key: 'notes', label: 'Notes', type: 'textarea' },
];

// Version fields minus the two we render as dedicated controls at the bottom.
const RATE_FIELDS = profileVersionFields.filter((f) => f.key !== 'effective_start' && f.key !== 'version_notes');

const todayIso = () => new Date().toISOString().slice(0, 10);

const currentVersion = (p) => (p.versions && p.versions.length ? p.versions[0] : null); // sorted desc

const isSuperseded = (v) => !!v.effective_end && String(v.effective_end) < String(v.effective_start);

/** Type-aware value normalization so "1.5" (string) == 1.5 (number) etc. */
const norm = (field, v) => {
  if (field.type === 'number') return (v === '' || v === null || v === undefined) ? null : Number(v);
  if (field.type === 'boolean') return !!v;
  return v === undefined || v === null ? '' : String(v).trim();
};

const fieldsChanged = (fields, form, original) => fields.some((f) => norm(f, form[f.key]) !== norm(f, (original || {})[f.key]));

const rateSummary = (v) => {
  if (!v) return '—';
  const bits = [];
  if (v.ss_lp_pct != null) bits.push(`SS ${v.ss_lp_pct}/${v.ss_bp_pct ?? '—'}`);
  if (v.cd_lp_tier1_pct != null) bits.push(`CD ${v.cd_lp_tier1_pct}→${v.cd_lp_tier2_pct ?? '—'}@${v.cd_tier_trigger ?? '—'}`);
  if (v.cd_flat != null) bits.push(`flat $${v.cd_flat}${v.cd_flat_tier2 != null ? `→$${v.cd_flat_tier2}` : ''}`);
  if (v.heloc_rate_pct != null) bits.push(`HELOC ${v.heloc_rate_pct}%`);
  return bits.join(' · ') || '—';
};

export default function ProfilesManager({ isSuperAdmin, onTryInCalculator }) {
  const toast = useToast();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const sectionColor = useColorModeValue('gray.600', 'gray.400');
  const cancelRef = useRef();

  const [rows, setRows] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // editor: { mode: 'create'|'edit', profile?, version?, form, effectiveDate, reason }
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    setLoadError(null);
    try {
      const { rows: data } = await payrollApi.manageReference({ entity: 'profiles', action: 'list' });
      setRows(data);
    } catch (err) {
      setLoadError(err.message);
    }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const q = search.trim().toLowerCase();
    return rows.filter((p) => {
      const v = currentVersion(p);
      if (!showInactive && v && v.status === 'Inactive') return false;
      if (!q) return true;
      return [p.lo_name, p.email, v && v.team, v && v.plan_type].some((s) => String(s || '').toLowerCase().includes(q));
    });
  }, [rows, search, showInactive]);

  const openCreate = () => {
    const form = {};
    RATE_FIELDS.forEach((f) => { if (f.default !== undefined) form[f.key] = f.default; });
    setEditor({ mode: 'create', form, effectiveDate: todayIso(), reason: '' });
  };

  const openEdit = (p) => {
    const v = currentVersion(p) || {};
    const form = {};
    IDENTITY_FIELDS.forEach((f) => { form[f.key] = p[f.key]; });
    RATE_FIELDS.forEach((f) => { form[f.key] = v[f.key]; });
    setEditor({ mode: 'edit', profile: p, version: v, form, effectiveDate: todayIso(), reason: '' });
  };

  // Live diff drives the "this will create a new version" hint + date control.
  const identityChanged = editor && editor.mode === 'edit' && fieldsChanged(IDENTITY_FIELDS, editor.form, editor.profile);
  const ratesChanged = editor && (editor.mode === 'create' || fieldsChanged(RATE_FIELDS, editor.form, editor.version));

  const save = async () => {
    const { mode, profile, form, effectiveDate, reason } = editor;
    const { values, badFields } = coerceNumericFields([...IDENTITY_FIELDS, ...RATE_FIELDS], form);
    if (badFields.length) {
      toast({ title: `Not a number: ${badFields.join(', ')}`, status: 'warning', duration: 4000 });
      return;
    }
    if ((mode === 'create' || ratesChanged) && !effectiveDate) {
      toast({ title: 'Effective date is required', status: 'warning', duration: 4000 });
      return;
    }
    setSaving(true);
    try {
      if (mode === 'create') {
        if (!values.lo_name || !values.email) throw new Error('Name and email are required.');
        await payrollApi.manageReference({
          entity: 'profiles',
          action: 'create',
          payload: { ...values, effective_start: effectiveDate, version_notes: reason || null },
          reason: reason || undefined,
        });
      } else {
        if (!identityChanged && !ratesChanged) {
          toast({ title: 'No changes to save', status: 'info', duration: 3000 });
          setSaving(false);
          return;
        }
        if (identityChanged) {
          const identityPayload = {};
          IDENTITY_FIELDS.forEach((f) => { identityPayload[f.key] = values[f.key]; });
          await payrollApi.manageReference({
            entity: 'profiles', action: 'update', id: profile.id, payload: identityPayload, reason: reason || undefined,
          });
        }
        if (ratesChanged) {
          const ratePayload = {};
          RATE_FIELDS.forEach((f) => { ratePayload[f.key] = values[f.key] === undefined ? null : values[f.key]; });
          ratePayload.effective_start = effectiveDate;
          ratePayload.version_notes = reason || null;
          await payrollApi.manageReference({
            entity: 'profiles', action: 'addVersion', id: profile.id, payload: ratePayload, reason: reason || undefined,
          });
        }
      }
      toast({ title: 'Saved', status: 'success', duration: 3000 });
      setEditor(null);
      await load();
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await payrollApi.manageReference({ entity: 'profiles', action: 'delete', id: deleting.id, reason: 'deleted via Reference UI' });
      toast({ title: 'Deleted', status: 'success', duration: 3000 });
      setDeleting(null);
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 8000, isClosable: true });
    } finally {
      setDeleteBusy(false);
    }
  };

  const exportCsv = () => {
    const csv = Papa.unparse(filtered.map((p) => {
      const v = currentVersion(p) || {};
      return {
        Name: p.lo_name, Email: p.email, Status: v.status, Plan: v.plan_type, 'W2/1099': v.w2_or_1099,
        Branch: v.branch, Team: v.team, 'SS LP %': v.ss_lp_pct, 'SS BP %': v.ss_bp_pct, 'SS fee': v.ss_processing_fee,
        'CD T1 %': v.cd_lp_tier1_pct, Trigger: v.cd_tier_trigger, 'CD T2 %': v.cd_lp_tier2_pct, 'CD BP %': v.cd_bp_pct,
        'CD flat': v.cd_flat, 'CD flat T2': v.cd_flat_tier2, 'HELOC %': v.heloc_rate_pct,
        'Broker flat': v.broker_referral_flat, 'BP floor': v.bp_revenue_floor, Effective: v.effective_start,
      };
    }));
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'payroll-lo-profiles.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (loadError) return <Alert status="error" borderRadius="md"><AlertIcon />{loadError}<Button ml={3} size="sm" onClick={load}>Retry</Button></Alert>;
  if (!rows) return <Flex justify="center" py={10}><Spinner /></Flex>;

  const editorTitle = editor && (editor.mode === 'create'
    ? 'New LO profile'
    : `Edit — ${editor.profile && editor.profile.lo_name}`);

  return (
    <Box>
      <Flex gap={2} mb={3} wrap="wrap" align="center">
        <Input size="sm" maxW="260px" placeholder="Search name, email, team…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Checkbox size="sm" isChecked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}>show inactive</Checkbox>
        <Box flex="1" />
        <Button size="sm" variant="outline" onClick={exportCsv}>Export CSV</Button>
        <Button size="sm" colorScheme="purple" onClick={openCreate}>New LO</Button>
      </Flex>

      <Box overflowX="auto" borderWidth="1px" borderRadius="md">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Name</Th><Th>Email</Th><Th>Status</Th><Th>Plan</Th><Th>W2</Th><Th>Branch</Th><Th>Rates (current)</Th><Th>Since</Th><Th />
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((p) => {
              const v = currentVersion(p);
              return (
                <Tr key={p.id} _hover={{ bg: hoverBg }}>
                  <Td whiteSpace="nowrap">{p.lo_name}{p.is_house_account && <Badge ml={1} colorScheme="gray">house</Badge>}</Td>
                  <Td maxW="200px" isTruncated title={p.email}>{p.email}</Td>
                  <Td>
                    <Badge colorScheme={v && v.status === 'Active' ? 'green' : v && v.status === 'VERIFY' ? 'orange' : 'gray'}>
                      {v ? v.status : '—'}
                    </Badge>
                  </Td>
                  <Td whiteSpace="nowrap">{v && v.plan_type ? v.plan_type : '—'}</Td>
                  <Td>{v && v.w2_or_1099 ? v.w2_or_1099 : '—'}</Td>
                  <Td>{v && v.branch ? v.branch : '—'}</Td>
                  <Td maxW="280px" isTruncated title={rateSummary(v)} fontSize="xs">{rateSummary(v)}</Td>
                  <Td whiteSpace="nowrap" fontSize="xs">{v ? v.effective_start : '—'}</Td>
                  <Td textAlign="right" whiteSpace="nowrap">
                    <Button size="xs" variant="outline" mr={1} onClick={() => openEdit(p)}>Edit</Button>
                    <Button size="xs" variant="ghost" mr={1} onClick={() => setHistoryFor(p)}>History</Button>
                    {onTryInCalculator && (
                      <Button size="xs" variant="ghost" colorScheme="blue" mr={1} onClick={() => onTryInCalculator(p.email)}>
                        Try in calculator
                      </Button>
                    )}
                    {isSuperAdmin && (
                      <Button size="xs" colorScheme="red" variant="ghost" onClick={() => setDeleting(p)}>Delete</Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* editor modal */}
      <Modal isOpen={!!editor} onClose={() => setEditor(null)} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editorTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editor && (
              <>
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={sectionColor} mb={2}>Identity</Text>
                <EditorFields fields={IDENTITY_FIELDS} form={editor.form} setForm={(fn) => setEditor((s) => ({ ...s, form: typeof fn === 'function' ? fn(s.form) : fn }))} />
                <Divider my={4} />
                <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" color={sectionColor} mb={2}>Comp rates</Text>
                <EditorFields fields={RATE_FIELDS} form={editor.form} setForm={(fn) => setEditor((s) => ({ ...s, form: typeof fn === 'function' ? fn(s.form) : fn }))} />
                <Divider my={4} />
                {editor.mode === 'edit' && ratesChanged && (
                  <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    Rate fields changed — saving creates a new version effective on the date below
                    and closes the current one. Runs use the version in effect on their run date;
                    the full timeline stays in History.
                  </Alert>
                )}
                {(editor.mode === 'create' || ratesChanged) && (
                  <FormControl mb={3} isRequired>
                    <FormLabel fontSize="sm">{editor.mode === 'create' ? 'First version effective date' : 'New version effective date'}</FormLabel>
                    <Input
                      size="sm" type="date" maxW="200px"
                      value={editor.effectiveDate}
                      min={editor.mode === 'edit' && editor.version ? editor.version.effective_start : undefined}
                      onChange={(e) => setEditor((s) => ({ ...s, effectiveDate: e.target.value }))}
                    />
                    {editor.mode === 'edit' && editor.version && editor.effectiveDate === editor.version.effective_start && (
                      <Text fontSize="xs" color="orange.400" mt={1}>
                        Same day as the current version — it will be superseded (kept in History, never used by runs).
                      </Text>
                    )}
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel fontSize="sm">Reason / notes (optional, shown in History and the audit log)</FormLabel>
                  <Textarea size="sm" rows={2} value={editor.reason}
                    placeholder="What changed and why (agreement, typo fix, who approved)…"
                    onChange={(e) => setEditor((s) => ({ ...s, reason: e.target.value }))} />
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={() => setEditor(null)}>Cancel</Button>
            <Button colorScheme="purple" onClick={save} isLoading={saving}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* history drawer */}
      <Drawer isOpen={!!historyFor} onClose={() => setHistoryFor(null)} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Version history — {historyFor && historyFor.lo_name}</DrawerHeader>
          <DrawerBody>
            {historyFor && (historyFor.versions || []).map((v) => (
              <Box key={v.id} borderWidth="1px" borderRadius="md" p={3} mb={3} opacity={isSuperseded(v) ? 0.6 : 1}>
                <Flex justify="space-between" mb={1}>
                  {isSuperseded(v) ? (
                    <Badge colorScheme="gray">superseded (was effective {v.effective_start})</Badge>
                  ) : (
                    <Badge colorScheme={!v.effective_end ? 'green' : 'gray'}>
                      {v.effective_start} → {v.effective_end || 'open'}
                    </Badge>
                  )}
                  <Badge>{v.status}</Badge>
                </Flex>
                <Text fontSize="xs"><Code fontSize="xs">{rateSummary(v)}</Code></Text>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {v.plan_type || '—'} · {v.w2_or_1099 || '—'} · fee waivers: corr {String(v.fee_waived_correspondent ?? true)}, HELOC {String(v.fee_waived_heloc_no_processor ?? true)}
                </Text>
                {v.version_notes && <Text fontSize="xs" mt={1}>{v.version_notes}</Text>}
              </Box>
            ))}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* delete confirm */}
      <AlertDialog isOpen={!!deleting} leastDestructiveRef={cancelRef} onClose={() => setDeleting(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete LO profile?</AlertDialogHeader>
            <AlertDialogBody>
              Deletes {deleting && deleting.lo_name} ({deleting && deleting.email}) and ALL rate
              versions (audit log keeps copies). Historical runs keep their snapshot. Prefer setting
              status = Inactive via Edit unless this profile was created in error.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleting(null)}>Cancel</Button>
              <Button colorScheme="red" ml={2} onClick={confirmDelete} isLoading={deleteBusy}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
